import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { observer } from "mobx-react-lite";
import { createPlugin, indexPlugins, statusPlugin, updatePlugin, updatePluginSetting } from "../http/PluginAPI";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import BreadCrumbs from "../components/Breadcrumbs";
import { PLUGIN_DETAILS_ROUTE } from "../utils/consts";
import { getApiErrorMessage } from "../http/apiError";
import { useAsyncData } from "../hooks/useAsyncData";
import PluginsTable from "./plugins/components/PluginsTable";
import { AddPluginModal, EditPluginModal } from "./plugins/components/PluginFormModal";

const defaultPluginForm = {
    provider: 1,
    name: '',
    version: '',
    file: null,
};

const Plugins = observer(() => {
    const navigate = useNavigate();
    const [openSettings, setOpenSettings] = useState(false);
    const [openAddPlugin, setOpenAddPlugin] = useState(false);
    const { data: plugins = [], setData: setPlugins, reload: loadPlugins } = useAsyncData(indexPlugins, {
        initialData: [],
        errorMessage: 'Ошибка загрузки плагинов',
    });
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('plugin_id');
    const [page, setPage] = useState(0);
    const [dense] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(9);
    const [selectedPlugin, setSelectedPlugin] = useState(null);
    const [pluginForm, setPluginForm] = useState(defaultPluginForm);


    const updateForm = (field, value) => {
        setPluginForm(prev => ({ ...prev, [field]: value }));
    };

    const openAddModal = () => {
        setPluginForm(defaultPluginForm);
        setOpenAddPlugin(true);
    };

    const closeAddModal = () => setOpenAddPlugin(false);
    const closeSettingsModal = () => setOpenSettings(false);

    const openSettingsModal = (plugin) => {
        setSelectedPlugin(plugin);
        setPluginForm({
            provider: plugin.provider_id ?? 1,
            name: plugin.plugin_name ?? plugin.name ?? '',
            version: plugin.version ?? '',
            file: null,
        });
        setOpenSettings(true);
    };

    const createFormData = (includePluginId = false) => {
        const formData = new FormData();
        if (includePluginId && selectedPlugin) formData.append('plugin_id', selectedPlugin.plugin_id);
        formData.append('provider', pluginForm.provider);
        formData.append('name', pluginForm.name);
        formData.append('version', pluginForm.version);
        if (pluginForm.file) formData.append('file', pluginForm.file);
        return formData;
    };

    const handleCreatePlugin = async () => {
        try {
            await createPlugin(createFormData());
            closeAddModal();
            await loadPlugins();
        } catch (e) {
            console.error(getApiErrorMessage(e));
        }
    };

    const handleUpdatePluginSettings = async () => {
        if (!selectedPlugin) return;

        try {
            await updatePluginSetting(createFormData(true));
            closeSettingsModal();
            await loadPlugins();
        } catch (e) {
            console.error(getApiErrorMessage(e));
        }
    };

    const handleToggleStatus = async (status, pluginId) => {
        setPlugins(prev => prev.map(item => item.plugin_id === pluginId ? { ...item, status } : item));
        try {
            await statusPlugin(pluginId, status);
        } catch (e) {
            console.error(getApiErrorMessage(e));
            await loadPlugins();
        }
    };

    const handleUpdatePlugin = async (pluginId) => {
        try {
            await updatePlugin(pluginId);
        } catch (e) {
            console.error(getApiErrorMessage(e));
        }
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <Container maxWidth="xl">
                <Box sx={{ width: '100%', marginTop: 3 }}>
                    <BreadCrumbs hrefs="" name="Список плагинов" />
                </Box>
                <Box sx={{ width: '100%', marginTop: 3 }}>
                    <Paper sx={{ width: '100%', mb: 2 }}>
                        <PluginsTable
                            plugins={plugins}
                            order={order}
                            orderBy={orderBy}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            dense={dense}
                            onRequestSort={handleRequestSort}
                            onChangePage={(event, newPage) => setPage(newPage)}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                            onAddPlugin={openAddModal}
                            onOpenDetails={(pluginId) => navigate(`${PLUGIN_DETAILS_ROUTE}/${pluginId}`)}
                            onToggleStatus={handleToggleStatus}
                            onOpenSettings={openSettingsModal}
                            onUpdatePlugin={handleUpdatePlugin}
                        />
                    </Paper>
                </Box>
            </Container>

            <AddPluginModal
                open={openAddPlugin}
                onClose={closeAddModal}
                form={pluginForm}
                onFormChange={updateForm}
                onSubmit={handleCreatePlugin}
            />
            <EditPluginModal
                open={openSettings}
                onClose={closeSettingsModal}
                form={pluginForm}
                onFormChange={updateForm}
                onSubmit={handleUpdatePluginSettings}
            />
        </>
    );
});

export default Plugins;
